import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop/types";

import {
  AspectButton,
  AspectButtonsWrapper,
  ButtonsWrapper,
  CropperWrapper,
  ModalContainer,
  ModalOverlay,
  SliderWrapper,
} from "./ImageCropModalStyles";
import getCroppedImg from "../../utils/getCroppedImg";
import ButtonGradient from "../ui/ButtonGradient";

interface ImageCropModalProps {
  imageSrc: string;
  onClose: () => void;
  onCropComplete: (croppedImage: string) => void;
}

type AspectOption = "1:1" | "4:5" | "16:9";

const aspectMap: Record<AspectOption, number> = {
  "1:1": 1 / 1,
  "4:5": 4 / 5,
  "16:9": 16 / 9,
};

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  imageSrc,
  onClose,
  onCropComplete,
}) => {
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const [aspectChoice, setAspectChoice] = useState<AspectOption>("4:5");

  const onCropAreaComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
    onCropComplete(croppedImage);
  };

  const handleAspectChange = (value: AspectOption) => {
    setAspectChoice(value);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <ModalOverlay>
      <ModalContainer>
        <CropperWrapper>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectMap[aspectChoice]}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropAreaComplete}
          />
        </CropperWrapper>

        <SliderWrapper>
          <AspectButtonsWrapper>
            {(["1:1", "4:5", "16:9"] as AspectOption[]).map((option) => (
              <AspectButton
                key={option}
                $active={aspectChoice === option}
                onClick={() => handleAspectChange(option)}
              >
                {option}
              </AspectButton>
            ))}
          </AspectButtonsWrapper>

          <label style={{ display: "block", marginBottom: 8 }}>Zoom</label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: "100%" }}
          />
        </SliderWrapper>

        <ButtonsWrapper>
          <ButtonGradient onClick={onClose}>Cancelar</ButtonGradient>
          <ButtonGradient onClick={handleConfirm}>Confirmar</ButtonGradient>
        </ButtonsWrapper>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ImageCropModal;
